import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { RootState, User } from '../../types';
import {
  fetchClientById,
  fetchClientAccess,
  addClientAccess,
  removeClientAccess,
  clearClientError
} from '../../store/slices/clientsSlice';
import api from '../../utils/axiosConfig';

const ClientAccessManagement: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedClient, isLoading, error } = useSelector((state: RootState) => state.clients);
  const { user } = useSelector((state: RootState) => state.auth);
  
  // State for users with access
  const [usersWithAccess, setUsersWithAccess] = useState<User[]>([]);
  // State for all users (for adding access)
  const [allUsers, setAllUsers] = useState<User[]>([]);
  // State for selected user to add
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  // State for add user dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  // State for remove access dialog
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  
  // Check if user is admin
  const isAdmin = user?.role === 'global_admin';
  
  // Fetch client and access data on component mount
  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientById(clientId) as any);
      fetchUsers();
      fetchAccessList();
    }
    
    // Cleanup on unmount
    return () => {
      dispatch(clearClientError());
    };
  }, [dispatch, clientId]);
  
  // Fetch all users (for admin to add access)
  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  // Fetch users with access to this client
  const fetchAccessList = () => {
    if (clientId) {
      dispatch(fetchClientAccess(clientId) as any).then((result: any) => {
        if (!result.error) {
          setUsersWithAccess(result.payload);
        }
      });
    }
  };
  
  // Handle back button click
  const handleBack = () => {
    navigate(`/clients/${clientId}`);
  };
  
  // Handle add access dialog open
  const handleAddDialogOpen = () => {
    setOpenAddDialog(true);
  };
  
  // Handle add access dialog close
  const handleAddDialogClose = () => {
    setOpenAddDialog(false);
    setSelectedUserId('');
  };
  
  // Handle user selection change
  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserId(event.target.value);
  };
  
  // Handle add access
  const handleAddAccess = () => {
    if (clientId && selectedUserId) {
      dispatch(addClientAccess({ clientId, userId: selectedUserId }) as any).then((result: any) => {
        if (!result.error) {
          handleAddDialogClose();
          fetchAccessList();
        }
      });
    }
  };
  
  // Handle remove access dialog open
  const handleRemoveDialogOpen = (user: User) => {
    setUserToRemove(user);
    setOpenRemoveDialog(true);
  };
  
  // Handle remove access dialog close
  const handleRemoveDialogClose = () => {
    setOpenRemoveDialog(false);
    setUserToRemove(null);
  };
  
  // Handle remove access
  const handleRemoveAccess = () => {
    if (clientId && userToRemove) {
      dispatch(removeClientAccess({ clientId, userId: userToRemove.id }) as any).then((result: any) => {
        if (!result.error) {
          handleRemoveDialogClose();
          fetchAccessList();
        }
      });
    }
  };
  
  // Clear error
  const handleClearError = () => {
    dispatch(clearClientError());
  };
  
  // Filter out users who already have access
  const availableUsers = allUsers.filter(
    user => !usersWithAccess.some(accessUser => accessUser.id === user.id)
  );
  
  // If loading and no client data, show loading indicator
  if (isLoading && !selectedClient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  // If no client found, show error
  if (!selectedClient && !isLoading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back to Client
        </Button>
        <Alert severity="error">
          Client not found or you don't have access to view it.
        </Alert>
      </Box>
    );
  }
  
  // If user is not admin, redirect
  if (!isAdmin) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back to Client
        </Button>
        <Alert severity="error">
          You don't have permission to manage client access.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Client
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddDialogOpen}
          disabled={availableUsers.length === 0}
        >
          Add User Access
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" onClose={handleClearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Manage Access for {selectedClient?.name}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : usersWithAccess.length === 0 ? (
          <Typography variant="body1" align="center">
            No users have access to this client.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersWithAccess.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveDialogOpen(user)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Add User Access Dialog */}
      <Dialog open={openAddDialog} onClose={handleAddDialogClose}>
        <DialogTitle>Add User Access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a user to grant access to this client.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUserId}
              label="User"
              onChange={handleUserChange}
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddAccess}
            color="primary"
            disabled={!selectedUserId}
          >
            Add Access
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Remove User Access Dialog */}
      <Dialog open={openRemoveDialog} onClose={handleRemoveDialogClose}>
        <DialogTitle>Remove User Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove access for {userToRemove?.first_name} {userToRemove?.last_name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveDialogClose}>Cancel</Button>
          <Button onClick={handleRemoveAccess} color="error">
            Remove Access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAccessManagement;